import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const formatDateTime = (d) => {
  if (!d) return '—';
  return new Date(d)?.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const SectionHeader = ({ icon, title }) => (
  <div className="flex items-center gap-2 mb-3">
    <Icon name={icon} size={16} className="text-primary" />
    <h3 className="text-sm font-semibold text-foreground">{title}</h3>
  </div>
);

const AccountDetailModal = ({ user, userType, onClose, onSuspend, onReactivate, onPasswordReset, onEscalate, onRefresh }) => {
  const [registrations, setRegistrations] = useState([]);
  const [events, setEvents] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [suspendReason, setSuspendReason] = useState('');
  const [showSuspendForm, setShowSuspendForm] = useState(false);
  const [escalationForm, setEscalationForm] = useState({ subject: '', description: '', priority: 'medium' });
  const [showEscalationForm, setShowEscalationForm] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  useEffect(() => {
    if (!user) return;
    setLoadingActivity(true);
    const fetchActivity = async () => {
      try {
        if (userType === 'parent') {
          const { data } = await supabase?.from('event_registrations')?.select('id, event_id, child_name, status, registered_at, total_amount, events(title, start_date, status)')?.eq('user_id', user?.id)?.order('registered_at', { ascending: false })?.limit(20);
          setRegistrations(data || []);
        } else if (userType === 'host') {
          const { data } = await supabase?.from('events')?.select('id, title, status, start_date, max_capacity, created_at')?.eq('host_id', user?.id)?.order('created_at', { ascending: false })?.limit(20);
          setEvents(data || []);
        }
      } catch (e) {
        // silently fail
      } finally {
        setLoadingActivity(false);
      }
    };
    fetchActivity();
  }, [user, userType]);

  const handleSuspend = async () => {
    if (!suspendReason?.trim()) return;
    setActionLoading('suspend');
    try {
      const table = userType === 'parent' ? 'parent_profiles' : 'event_host_profiles';
      const { error } = await supabase?.from(table)?.update({
        is_suspended: true,
        suspended_at: new Date()?.toISOString(),
        suspended_reason: suspendReason?.trim(),
      })?.eq('id', user?.id);
      if (error) throw error;
      showToast('Account suspended successfully');
      setShowSuspendForm(false);
      setSuspendReason('');
      onSuspend?.(user?.id);
      onRefresh?.();
    } catch (e) {
      showToast(e?.message || 'Failed to suspend account', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivate = async () => {
    setActionLoading('reactivate');
    try {
      const table = userType === 'parent' ? 'parent_profiles' : 'event_host_profiles';
      const { error } = await supabase?.from(table)?.update({
        is_suspended: false,
        suspended_at: null,
        suspended_reason: null,
      })?.eq('id', user?.id);
      if (error) throw error;
      showToast('Account reactivated successfully');
      onReactivate?.(user?.id);
      onRefresh?.();
    } catch (e) {
      showToast(e?.message || 'Failed to reactivate account', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePasswordReset = async () => {
    setActionLoading('reset');
    try {
      const { error } = await supabase?.auth?.resetPasswordForEmail(user?.email, {
        redirectTo: `${window.location?.origin}/reset-password`,
      });
      if (error) throw error;
      showToast('Password reset email sent successfully');
      onPasswordReset?.(user?.id);
    } catch (e) {
      showToast(e?.message || 'Failed to send password reset', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  const handleEscalate = async () => {
    if (!escalationForm?.subject?.trim() || !escalationForm?.description?.trim()) return;
    setActionLoading('escalate');
    try {
      const { error } = await supabase?.from('support_escalations')?.insert({
        user_id: user?.id,
        user_type: userType,
        subject: escalationForm?.subject?.trim(),
        description: escalationForm?.description?.trim(),
        priority: escalationForm?.priority,
        status: 'open',
      });
      if (error) throw error;
      showToast('Support escalation created successfully');
      setShowEscalationForm(false);
      setEscalationForm({ subject: '', description: '', priority: 'medium' });
      onEscalate?.(user?.id);
    } catch (e) {
      showToast(e?.message || 'Failed to create escalation', 'error');
    } finally {
      setActionLoading(null);
    }
  };

  if (!user) return null;

  const isSuspended = user?.is_suspended;
  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'User' },
    { id: 'activity', label: userType === 'parent' ? 'Registrations' : 'Events', icon: userType === 'parent' ? 'Calendar' : 'LayoutGrid' },
    { id: 'actions', label: 'Admin Actions', icon: 'ShieldCheck' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-background rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-border">
        {/* Toast */}
        {toast && (
          <div className={`absolute top-4 right-4 z-50 flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium shadow-lg ${
            toast?.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
          }`}>
            <Icon name={toast?.type === 'error' ? 'XCircle' : 'CheckCircle2'} size={16} />
            {toast?.msg}
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${
              userType === 'parent' ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-green-100 dark:bg-green-900/30'
            }`}>
              {user?.avatar_url ? (
                <img src={user?.avatar_url} alt={user?.full_name} className="w-full h-full rounded-full object-cover" />
              ) : (
                <Icon name={userType === 'parent' ? 'User' : 'Building2'} size={20} className={userType === 'parent' ? 'text-blue-600' : 'text-green-600'} />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-foreground">{user?.full_name || '—'}</h2>
                {isSuspended && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                    <Icon name="Ban" size={10} /> Suspended
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-colors">
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border flex-shrink-0">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab?.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={14} />
              {tab?.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div className="card p-4 space-y-2">
                  <SectionHeader icon="Info" title="Account Info" />
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Phone</span>
                      <span className="text-foreground font-medium">{user?.phone || '—'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Joined</span>
                      <span className="text-foreground font-medium">{formatDate(user?.created_at)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Updated</span>
                      <span className="text-foreground font-medium">{formatDate(user?.updated_at)}</span>
                    </div>
                    {userType === 'host' && (
                      <>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Organization</span>
                          <span className="text-foreground font-medium truncate max-w-[120px]">{user?.organization_name || '—'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Verification</span>
                          <span className="text-foreground font-medium capitalize">{user?.verification_status || '—'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Rating</span>
                          <span className="text-foreground font-medium flex items-center gap-1">
                            <Icon name="Star" size={12} className="text-amber-500 fill-amber-500" />
                            {user?.avg_rating ? Number(user?.avg_rating)?.toFixed(1) : '—'}
                            {user?.total_reviews > 0 && <span className="text-muted-foreground">({user?.total_reviews})</span>}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="card p-4 space-y-2">
                  <SectionHeader icon="Activity" title="Account Status" />
                  <div className="space-y-2">
                    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
                      isSuspended ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300' : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                    }`}>
                      <Icon name={isSuspended ? 'Ban' : 'CheckCircle2'} size={16} />
                      <span className="font-medium">{isSuspended ? 'Suspended' : 'Active'}</span>
                    </div>
                    {isSuspended && user?.suspended_at && (
                      <p className="text-xs text-muted-foreground">Suspended on {formatDateTime(user?.suspended_at)}</p>
                    )}
                    {isSuspended && user?.suspended_reason && (
                      <p className="text-xs text-muted-foreground">Reason: {user?.suspended_reason}</p>
                    )}
                  </div>
                </div>
              </div>
              {userType === 'host' && user?.bio && (
                <div className="card p-4">
                  <SectionHeader icon="FileText" title="Bio" />
                  <p className="text-sm text-muted-foreground">{user?.bio}</p>
                </div>
              )}
            </div>
          )}

          {/* Activity Tab */}
          {activeTab === 'activity' && (
            <div>
              {loadingActivity ? (
                <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground">
                  <Icon name="Loader2" size={20} className="animate-spin" />
                  <span className="text-sm">Loading activity...</span>
                </div>
              ) : userType === 'parent' ? (
                registrations?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
                    <Icon name="Calendar" size={36} />
                    <p className="text-sm">No registrations found</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground mb-3">{registrations?.length} registration(s) found</p>
                    {registrations?.map(reg => (
                      <div key={reg?.id} className="card p-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{reg?.events?.title || 'Unknown Event'}</p>
                          <p className="text-xs text-muted-foreground">Child: {reg?.child_name} · {formatDate(reg?.registered_at)}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {reg?.total_amount > 0 && (
                            <span className="text-xs font-medium text-foreground">${Number(reg?.total_amount)?.toFixed(2)}</span>
                          )}
                          <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
                            reg?.status === 'confirmed' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                            : reg?.status === 'cancelled'? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                          }`}>
                            {reg?.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              ) : (
                events?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-2 text-muted-foreground">
                    <Icon name="LayoutGrid" size={36} />
                    <p className="text-sm">No events found</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground mb-3">{events?.length} event(s) found</p>
                    {events?.map(ev => (
                      <div key={ev?.id} className="card p-3 flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{ev?.title}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(ev?.start_date)} · Capacity: {ev?.max_capacity || '—'}</p>
                        </div>
                        <span className={`px-2 py-0.5 text-xs rounded-full font-medium flex-shrink-0 ${
                          ev?.status === 'approved' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                          : ev?.status === 'pending_review' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                          : ev?.status === 'rejected'? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' :'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                        }`}>
                          {ev?.status?.replace(/_/g, ' ')}
                        </span>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          )}

          {/* Admin Actions Tab */}
          {activeTab === 'actions' && (
            <div className="space-y-4">
              {/* Suspend / Reactivate */}
              <div className="card p-4">
                <SectionHeader icon="Ban" title="Account Status Control" />
                {!isSuspended ? (
                  <>
                    {!showSuspendForm ? (
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="UserX"
                        iconPosition="left"
                        onClick={() => setShowSuspendForm(true)}
                        className="border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/20"
                      >
                        Suspend Account
                      </Button>
                    ) : (
                      <div className="space-y-3">
                        <textarea
                          value={suspendReason}
                          onChange={e => setSuspendReason(e?.target?.value)}
                          placeholder="Reason for suspension (required)..."
                          rows={3}
                          className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                        />
                        <div className="flex gap-2">
                          <Button
                            variant="destructive"
                            size="sm"
                            iconName="UserX"
                            iconPosition="left"
                            onClick={handleSuspend}
                            disabled={!suspendReason?.trim() || actionLoading === 'suspend'}
                          >
                            {actionLoading === 'suspend' ? 'Suspending...' : 'Confirm Suspend'}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => { setShowSuspendForm(false); setSuspendReason(''); }}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                      <Icon name="Ban" size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-red-700 dark:text-red-300">Account is suspended</p>
                        {user?.suspended_reason && <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">Reason: {user?.suspended_reason}</p>}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="UserCheck"
                      iconPosition="left"
                      onClick={handleReactivate}
                      disabled={actionLoading === 'reactivate'}
                      className="border-green-200 text-green-600 hover:bg-green-50 dark:border-green-800 dark:text-green-400 dark:hover:bg-green-900/20"
                    >
                      {actionLoading === 'reactivate' ? 'Reactivating...' : 'Reactivate Account'}
                    </Button>
                  </div>
                )}
              </div>

              {/* Password Reset */}
              <div className="card p-4">
                <SectionHeader icon="KeyRound" title="Password Reset" />
                <p className="text-xs text-muted-foreground mb-3">Send a password reset email to <span className="font-medium text-foreground">{user?.email}</span></p>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Mail"
                  iconPosition="left"
                  onClick={handlePasswordReset}
                  disabled={actionLoading === 'reset'}
                >
                  {actionLoading === 'reset' ? 'Sending...' : 'Send Password Reset Email'}
                </Button>
              </div>

              {/* Support Escalation */}
              <div className="card p-4">
                <SectionHeader icon="AlertTriangle" title="Support Escalation" />
                {!showEscalationForm ? (
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={() => setShowEscalationForm(true)}
                  >
                    Create Escalation
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <input
                      type="text"
                      value={escalationForm?.subject}
                      onChange={e => setEscalationForm(f => ({ ...f, subject: e?.target?.value }))}
                      placeholder="Subject..."
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    />
                    <textarea
                      value={escalationForm?.description}
                      onChange={e => setEscalationForm(f => ({ ...f, description: e?.target?.value }))}
                      placeholder="Describe the issue..."
                      rows={3}
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                    />
                    <select
                      value={escalationForm?.priority}
                      onChange={e => setEscalationForm(f => ({ ...f, priority: e?.target?.value }))}
                      className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                    >
                      <option value="low">Low Priority</option>
                      <option value="medium">Medium Priority</option>
                      <option value="high">High Priority</option>
                      <option value="urgent">Urgent</option>
                    </select>
                    <div className="flex gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        iconName="Send"
                        iconPosition="left"
                        onClick={handleEscalate}
                        disabled={!escalationForm?.subject?.trim() || !escalationForm?.description?.trim() || actionLoading === 'escalate'}
                      >
                        {actionLoading === 'escalate' ? 'Submitting...' : 'Submit Escalation'}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => { setShowEscalationForm(false); setEscalationForm({ subject: '', description: '', priority: 'medium' }); }}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountDetailModal;
